import { useMemo, useCallback } from 'react';
import handleNaN from '@/utils/handleNaN';
import useUrlState from '../../../utils/useUrlState';
import { TakerMarket } from '../../data/useTakerMarkets';

export type FilterSource = 'card' | 'filter';

export interface FilterState {
  customer?: string;
  participation?: string;
  accepted?: string;
  participationSource?: FilterSource;
  acceptedSource?: FilterSource;
}

export const PARTICIPATION_FILTER = {
  ON: 'on',
  OFF: 'off',
} as const;

export const ACCEPTED_FILTER = {
  ACCEPTED: 'accepted',
  NOT_ACCEPTED: 'not-accepted',
} as const;

export const useOfferActivityFilterLogic = (takerMarkets: TakerMarket[], initialMakerOrganizationUuid?: string) => {
  const [filters, setFilters] = useUrlState<FilterState>({
    customer: initialMakerOrganizationUuid || undefined,
    participation: undefined,
    accepted: undefined,
    participationSource: undefined,
    acceptedSource: undefined,
  });

  const setMakerOrganization = useCallback(
    (uuid: string) => {
      setFilters((prev) => ({
        ...prev,
        customer: uuid || undefined,
      }));
    },
    [setFilters]
  );

  const setParticipation = useCallback(
    (participation: string, source?: FilterSource) => {
      setFilters((prev) => {
        // if source is card, clear all other filters
        if (source === 'card' && participation) {
          return {
            customer: undefined,
            participation,
            accepted: undefined,
            participationSource: source,
            acceptedSource: undefined,
          };
        }

        return {
          ...prev,
          participation: participation || undefined,
          participationSource: participation ? source : undefined,
        };
      });
    },
    [setFilters]
  );

  const setAccepted = useCallback(
    (accepted: string, source?: FilterSource) => {
      setFilters((prev) => {
        // if source is card, clear all other filters
        if (source === 'card' && accepted) {
          return {
            customer: undefined,
            participation: undefined,
            accepted,
            participationSource: undefined,
            acceptedSource: source,
          };
        }

        return {
          ...prev,
          accepted: accepted === '' ? undefined : accepted,
          acceptedSource: accepted ? source : undefined,
        };
      });
    },
    [setFilters]
  );

  const clearFilters = useCallback(() => {
    setFilters({
      customer: undefined,
      participation: undefined,
      accepted: undefined,
      participationSource: undefined,
      acceptedSource: undefined,
    });
  }, [setFilters]);

  const availableCustomers = useMemo(() => {
    const customersMap = new Map<string, string>();
    takerMarkets.forEach((market) => {
      if (market.makerOrganizationUuid && market.makerOrganizationName) {
        customersMap.set(market.makerOrganizationUuid, market.makerOrganizationName);
      }
    });

    return Array.from(customersMap.entries())
      .map(([uuid, name]) => ({ uuid, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [takerMarkets]);

  const filteredTakerMarkets = useMemo(() => {
    return takerMarkets.filter((takerMarket) => {
      const matchesCustomer = !filters.customer || takerMarket.makerOrganizationUuid === filters.customer;
      const matchesParticipation =
        !filters.participation ||
        (filters.participation === PARTICIPATION_FILTER.ON && takerMarket.offerConfig.isEnabled) ||
        (filters.participation === PARTICIPATION_FILTER.OFF && !takerMarket.offerConfig.isEnabled);
      let matchesAccepted = true;
      if (filters.accepted === ACCEPTED_FILTER.ACCEPTED) {
        matchesAccepted =
          !!takerMarket.offerConfig.isEnabled &&
          takerMarket.totalInvoiceCount > 0 &&
          handleNaN(takerMarket.clearingInvoiceAmount / takerMarket.eligibleInvoiceAmount) === 1;
      } else if (filters.accepted === ACCEPTED_FILTER.NOT_ACCEPTED) {
        matchesAccepted =
          !!takerMarket.offerConfig.isEnabled &&
          takerMarket.totalInvoiceCount > 0 &&
          handleNaN(takerMarket.clearingInvoiceAmount / takerMarket.eligibleInvoiceAmount) < 1;
      }

      return matchesCustomer && matchesParticipation && matchesAccepted;
    });
  }, [takerMarkets, filters.customer, filters.participation, filters.accepted]);

  const totalCustomerCount = useMemo(() => {
    return new Set(takerMarkets.map((m) => m.makerOrganizationUuid)).size;
  }, [takerMarkets]);

  const filteredCustomerCount = useMemo(() => {
    return new Set(filteredTakerMarkets.map((m) => m.makerOrganizationUuid)).size;
  }, [filteredTakerMarkets]);

  return useMemo(
    () => ({
      filters,
      takerMarkets,
      filteredTakerMarkets,
      availableCustomers,
      totalCustomerCount,
      totalDivisionCount: takerMarkets.length,
      filteredCustomerCount,
      filteredDivisionCount: filteredTakerMarkets.length,
      setMakerOrganization,
      setParticipation,
      setAccepted,
      clearFilters,
    }),
    [
      filters,
      takerMarkets,
      filteredTakerMarkets,
      availableCustomers,
      totalCustomerCount,
      filteredCustomerCount,
      setMakerOrganization,
      setParticipation,
      setAccepted,
      clearFilters,
    ]
  );
};
